import React, { useEffect, useState } from "react";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, Loader2, FileText, Film, Image as ImgIcon, FileSpreadsheet } from "lucide-react";

const ICON = {
  pdf: FileText, docx: FileText, txt: FileText, md: FileText, pptx: FileText,
  xlsx: FileSpreadsheet, csv: FileSpreadsheet,
  png: ImgIcon, jpg: ImgIcon, jpeg: ImgIcon, gif: ImgIcon, webp: ImgIcon,
  mp4: Film, mov: Film,
};
const CAT_LABEL = {
  material_apoyo: "Material de apoyo", lectura: "Lectura", ejercicio: "Ejercicio",
  video: "Video", otro: "Otro",
};

export default function AlumnoBiblioteca() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/alumno/enrollments").then((r) => {
      setPrograms(r.data || []);
      if (r.data?.length && !programId) setProgramId(r.data[0].program_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!programId) return;
    setLoading(true);
    api.get(`/alumno/programs/${programId}/library`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [programId]);

  const byCat = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  return (
    <AlumnoLayout user={user}>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Alumno</p>
        <h1 className="mt-1 text-3xl font-bold text-white font-display">Biblioteca del programa</h1>
        <p className="mt-1 text-sm text-gray-400">Material publicado por tu instructor.</p>
      </div>

      {programs.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-sm text-gray-400">
          Aún no tienes programas inscritos.
        </div>
      ) : (
        <>
          <div className="mb-6">
            <label className="text-xs font-semibold text-gray-300">Programa</label>
            <select value={programId} onChange={(e) => setProgramId(e.target.value)}
                    data-testid="alumno-biblio-program"
                    className="mt-1 w-full max-w-sm rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
              {programs.map(p => <option key={p.program_id} value={p.program_id}>{p.program_title}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-sm text-gray-400">
              <FolderOpen className="w-8 h-8 text-gray-500 mx-auto mb-2" />
              Aún no hay material publicado en este programa.
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(byCat).map(([cat, list]) => (
                <div key={cat}>
                  <h2 className="text-sm uppercase tracking-widest text-gray-400 font-bold mb-2">{CAT_LABEL[cat] || cat}</h2>
                  <div className="grid md:grid-cols-2 gap-2">
                    {list.map((it) => {
                      const ext = (it.filename || "").split(".").pop().toLowerCase();
                      const Icon = ICON[ext] || FileText;
                      return (
                        <a key={it.id}
                           href={`${BACKEND_URL}${it.url}`}
                           target="_blank" rel="noreferrer"
                           data-testid={`alumno-biblio-row-${it.id}`}
                           className="rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/[0.08] flex items-start gap-3">
                          <Icon className="w-5 h-5 text-[#F26A21] flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-white truncate">{it.title}</p>
                            {it.description && <p className="mt-0.5 text-xs text-gray-400 line-clamp-2">{it.description}</p>}
                            <p className="mt-1 text-[11px] text-gray-500">{it.filename} · {Math.max(1, Math.round((it.size || 0) / 1024))} KB</p>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </AlumnoLayout>
  );
}
